import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Star, Eye, EyeOff, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { author: "", role: "", location: "", quote: "", rating: 5, photo_url: "", featured: true };

export default function AdminTestimonials() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/testimonials");
            setItems(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const open = (item = null) => {
        setEditing(item);
        setForm(item ? { ...item } : EMPTY);
    };
    const close = () => { setEditing(undefined); setForm(EMPTY); };

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing && editing.id) {
                const { data } = await api.put(`/admin/testimonials/${editing.id}`, form);
                setItems(items.map((i) => (i.id === data.id ? data : i)));
                toast.success("Témoignage modifié");
            } else {
                const { data } = await api.post("/admin/testimonials", form);
                setItems([data, ...items]);
                toast.success("Témoignage ajouté");
            }
            close();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const toggleFeatured = async (item) => {
        try {
            const { data } = await api.put(`/admin/testimonials/${item.id}`, { ...item, featured: !item.featured });
            setItems(items.map((i) => (i.id === data.id ? data : i)));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Supprimer le témoignage de "${item.author}" ?`)) return;
        await api.delete(`/admin/testimonials/${item.id}`);
        setItems(items.filter((i) => i.id !== item.id));
        toast.success("Témoignage supprimé");
    };

    return (
        <div className="p-10" data-testid="admin-testimonials-page">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <span className="overline">Espace admin</span>
                    <h1 className="mt-3 font-serif text-4xl text-brand-ink font-light">Témoignages clients</h1>
                </div>
                <button
                    onClick={() => open()}
                    data-testid="testimonial-new"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-brand-ink text-brand-bg text-xs uppercase tracking-[0.2em] hover:bg-brand-sage transition-colors"
                >
                    <Plus size={14} /> Nouveau témoignage
                </button>
            </div>

            {loading ? (
                <p className="mt-12 text-brand-sageDark">Chargement…</p>
            ) : items.length === 0 ? (
                <div className="mt-12 py-16 text-center bg-brand-bgalt border border-brand-border">
                    <p className="font-serif text-xl text-brand-ink italic">Aucun témoignage pour l'instant.</p>
                </div>
            ) : (
                <div className="mt-10 grid md:grid-cols-2 gap-6">
                    {items.map((t) => (
                        <div key={t.id} className="bg-brand-bgalt border border-brand-border p-6" data-testid={`testimonial-row-${t.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="font-serif text-lg text-brand-ink">{t.author}</p>
                                    <p className="text-xs text-brand-sageDark mt-0.5">{[t.role, t.location].filter(Boolean).join(" · ") || "—"}</p>
                                </div>
                                <div className="flex gap-0.5">
                                    {Array.from({ length: t.rating }).map((_, idx) => (
                                        <Star key={idx} size={12} className="text-brand-sage fill-brand-sage" />
                                    ))}
                                </div>
                            </div>
                            <p className="mt-4 text-sm text-brand-sageDark italic line-clamp-3">« {t.quote} »</p>
                            <div className="mt-4 flex items-center justify-between text-xs">
                                <span className={`px-2 py-1 ${t.featured ? "bg-brand-sage text-brand-bg" : "border border-brand-border text-brand-sageDark"}`}>
                                    {t.featured ? "Visible sur le site" : "Masqué"}
                                </span>
                                <div className="flex gap-1">
                                    <button onClick={() => toggleFeatured(t)} className="p-2 hover:bg-brand-sage/10" title="Basculer visibilité">
                                        {t.featured ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                    <button onClick={() => open(t)} className="p-2 hover:bg-brand-sage/10" title="Modifier">
                                        <Pencil size={14} />
                                    </button>
                                    <button onClick={() => remove(t)} className="p-2 hover:bg-red-50 text-red-700" title="Supprimer">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing !== undefined && (
                <div className="fixed inset-0 z-50 bg-brand-ink/60 flex items-center justify-center px-6" data-testid="testimonial-modal">
                    <form onSubmit={save} className="bg-brand-bg max-w-xl w-full p-8 md:p-10 border border-brand-border relative space-y-5">
                        <button type="button" onClick={close} className="absolute top-4 right-4 text-brand-sageDark hover:text-brand-ink">
                            <X size={18} />
                        </button>
                        <h2 className="font-serif text-2xl text-brand-ink font-light">
                            {editing ? "Modifier" : "Nouveau"} témoignage
                        </h2>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="overline block mb-2">Nom *</label>
                                <input required value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="field-input" />
                            </div>
                            <div>
                                <label className="overline block mb-2">Rôle</label>
                                <input value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} className="field-input" placeholder="Propriétaire, architecte…" />
                            </div>
                            <div>
                                <label className="overline block mb-2">Localisation</label>
                                <input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} className="field-input" placeholder="Paris 16e" />
                            </div>
                            <div>
                                <label className="overline block mb-2">Note</label>
                                <select value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} className="field-input bg-transparent">
                                    {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoile{n > 1 && "s"}</option>)}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="overline block mb-2">Témoignage *</label>
                            <textarea required rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="field-input field-textarea" />
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-brand-sage" />
                            <span>Visible sur le site public</span>
                        </label>
                        <div className="flex justify-end gap-3 pt-4">
                            <button type="button" onClick={close} className="px-5 py-2 text-xs uppercase tracking-[0.2em] text-brand-sageDark">Annuler</button>
                            <button type="submit" className="px-7 py-2.5 bg-brand-ink text-brand-bg text-xs uppercase tracking-[0.2em] hover:bg-brand-sage">
                                Enregistrer
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
